<script setup lang="ts">
import { computed, ref } from "vue";
import { Banknote, Calculator, CircleAlert, Info, TrendingUp } from "@lucide/vue";

const streams = ref(100000);
const rate = ref(0.018);
const monthlyCost = ref(119);
const releaseCost = ref(0);
const catalogSize = ref(12);

const gross = computed(() => streams.value * rate.value);
const net = computed(() => gross.value - monthlyCost.value - releaseCost.value);
const breakEven = computed(() => Math.ceil((monthlyCost.value + releaseCost.value) / Math.max(rate.value, 0.0001)));
const perTrack = computed(() => Math.round(streams.value / Math.max(catalogSize.value, 1)));
</script>

<template>
  <div class="revenue-layout">
    <section class="panel calculator-panel">
      <div class="section-heading border-bottom">
        <div><p class="eyebrow">UNIT ECONOMICS</p><h3>把“想赚钱”算成可执行目标</h3></div>
        <span class="outline-icon"><Calculator :size="18" /></span>
      </div>
      <div class="form-section two-column-form">
        <label class="field"><span>目标月播放量</span><input v-model.number="streams" min="0" step="1000" type="number" /></label>
        <label class="field"><span>自定义每次播放净收入（¥）</span><input v-model.number="rate" min="0" step="0.001" type="number" /></label>
        <label class="field"><span>每月工具成本（¥）</span><input v-model.number="monthlyCost" min="0" type="number" /></label>
        <label class="field"><span>发行 / 推广成本（¥）</span><input v-model.number="releaseCost" min="0" type="number" /></label>
        <label class="field"><span>在架作品数</span><input v-model.number="catalogSize" min="1" type="number" /></label>
      </div>
      <div class="estimate-hero">
        <span>预计月净收益</span>
        <strong :class="{ negative: net < 0 }">{{ net >= 0 ? "+" : "−" }}¥{{ Math.abs(net).toLocaleString("zh-CN", { maximumFractionDigits: 0 }) }}</strong>
        <p>毛收入 ¥{{ gross.toLocaleString("zh-CN", { maximumFractionDigits: 0 }) }} − 成本 ¥{{ (monthlyCost + releaseCost).toLocaleString() }}</p>
      </div>
      <div class="assumption-note"><Info :size="17" /><p><strong>这是经营模型，不是收益承诺。</strong>各平台、地区、订阅类型、发行商分成与税费差异很大，请用你发行商的真实结算数据更新“每次播放净收入”。</p></div>
    </section>

    <aside class="revenue-side">
      <article class="panel goal-card tone-lime">
        <Banknote :size="21" />
        <span>回本播放量</span>
        <strong>{{ breakEven.toLocaleString() }}</strong>
        <p>按当前成本与单次净收入估算</p>
      </article>
      <article class="panel goal-card">
        <TrendingUp :size="21" />
        <span>每首月均目标</span>
        <strong>{{ perTrack.toLocaleString() }}</strong>
        <p>{{ catalogSize }} 首作品共同承担目标</p>
      </article>
      <article class="panel reality-card">
        <CircleAlert :size="20" />
        <h3>真正影响收益的 4 件事</h3>
        <ol>
          <li><span>01</span>持续发行可搜索的作品</li>
          <li><span>02</span>提升完整播放与收藏</li>
          <li><span>03</span>获得歌单与短视频曝光</li>
          <li><span>04</span>核对发行商结算与版权归属</li>
        </ol>
      </article>
    </aside>
  </div>
</template>
